import {Directive, effect, EffectRef, ElementRef, model, OnDestroy, Optional, Renderer2, RendererStyleFlags2, TemplateRef, untracked, ViewContainerRef} from '@angular/core';
import {AceLogger} from '@app/logging/ace-logger';
import {AceLoggerService} from '@app/logging/ace-logger.service';

/** 
 * ### Description:
 *   - Manage the timing of animation and element creation/removal or css show/hide. Animates elements after they enter and before they leave.
 * 
 * ### Usage:
 * #### Use on `ng-templates`
 * - Uses `viewContainerRef.createEmbededView` to render `ng-template` content it is applied on.
 * - If condition is true, will render the template. If false, will remove the rendered content from DOM tree.
   ```html
    <!-- shorthand ng-template asterisk syntax -->
    <div *appNgIfAnimate="condition; classOnEnter: 'enter-ani'; classOnLeave: 'leave-ani'"></div>
    <!-- on ng-template -->
    <ng-template [appNgIfAnimate]="condition" appNgIfAnimateClassOnEnter="enter-ani" appNgIfAnimateClassOnLeave="leave-ani">
      <div></div>
    </ng-template>
   ```
 *
 * #### Use on `HTMlElement`
 * - Uses inline style `display: none !important;`
 * - If condition is true, will remove the style. If false, will add the style
   ```html
     <div [appNgIfAnimate]="condition" appNgIfAnimateClassOnEnter="enter-ani" appNgIfAnimateClassOnLeave="leave-ani"></div>
   ```
 * ### Notes:
 * When using animation styles that uses `transforms`, check that the element this is used on is a transformable element.
 */
@Directive({selector: '[appNgIfAnimate]'})
export class NgIfAnimateDirective implements OnDestroy {
  shouldShow = model<boolean>(false, {alias: 'appNgIfAnimate'});
  appNgIfAnimateClass = model<string>();
  appNgIfAnimateClassOnEnter = model<string>();
  appNgIfAnimateClassOnLeave = model<string>();

  /** The element the structural directive is applied on or the first element if ng-template is used. This will be set when element is shown. */
  private rootElement: HTMLElement | null = null;
  private readonly ANIMATION_TIMEOUT_MS = 500;
  private readonly ANIMATION_START_EVENT_NAME: keyof HTMLElementEventMap = 'animationstart';
  private readonly ANIMATION_END_EVENT_NAME: keyof HTMLElementEventMap = 'animationend';
  private readonly logger: AceLogger;

  private readonly animateEffect: EffectRef;
  private readonly animationClassUpdateEffect: EffectRef;

  constructor(private renderer: Renderer2,
              @Optional() templateRef: TemplateRef<any>,
              elementRef: ElementRef<HTMLElement>,
              viewContainerRef: ViewContainerRef,
              aceLoggerService: AceLoggerService) {
    this.logger = aceLoggerService.create(NgIfAnimateDirective.name);

    let showElement: () => void;
    let hideElement: () => void;
    const isNgIfMode = !!templateRef;
    /** Change show hide strategies depending on if the directive is on an ng-template or an actual element.
     * On ng-template, render or unrender an element.
     * On actual elements, use display none to hide an element. Using css styles instead of classes because it is
     *   less likely to conflict with how we apply css. */
    if (isNgIfMode) {
      showElement = () => {
        viewContainerRef.clear();
        const viewRef = viewContainerRef.createEmbeddedView(templateRef);
        this.rootElement = viewRef.rootNodes.find((node: Node) => node.nodeType === Node.ELEMENT_NODE);
        const appAnimateClass = untracked(() => this.appNgIfAnimateClass());
        if (appAnimateClass) {
          this.renderer.addClass(this.rootElement, appAnimateClass);
        }
      };
      hideElement = () => {
        viewContainerRef.clear();
        this.rootElement = null;
      };
    } else {
      this.rootElement = elementRef.nativeElement;
      const appNgIfAnimateClass = this.appNgIfAnimateClass();
      if (appNgIfAnimateClass) {
        this.renderer.addClass(this.rootElement, appNgIfAnimateClass);
      }
      const displayCssProperty: keyof CSSStyleDeclaration = 'display';
      showElement = () => {
        if (this.rootElement?.style.display) {
          this.renderer.removeStyle(this.rootElement, displayCssProperty);
        }
      };
      hideElement = () => {
        const currentDisplay = this.rootElement?.style.display;
        if (currentDisplay) {
          this.logger.error('Current inline style `display` of the element is already set with value of ' + currentDisplay + ' . Overwriting with none.');
        }
        this.renderer.setStyle(this.rootElement, displayCssProperty, 'none', RendererStyleFlags2.Important);
      };
    }

    // For skipping animation on first run.
    let firstRun = true;
    /** When rendering template, initially add on-enter animation class and remove when animation is done
     * On leave, add on-leave animation class and remove rendered template when animation is done. */
    this.animateEffect = effect(cleanUp => {
      const shouldShow = !!this.shouldShow();
      let removeListener: () => void;
      if (shouldShow) {
        const classOnEnter = untracked(() => this.appNgIfAnimateClassOnEnter());
        showElement?.();
        if (!firstRun) {
          if (this.rootElement && classOnEnter) {
              removeListener = this.onAnimationEnd(this.rootElement, () => this.renderer.removeClass(this.rootElement, classOnEnter));
              this.renderer.addClass(this.rootElement, classOnEnter);
          } else {
            if (!this.rootElement) {
              this.logger.error('No element found where the animation class could be added');
            } else if (!classOnEnter) {
              this.logger.error('No animation class for entering was specified');
            } else {
              this.logger.error('Unknown error during animation');
            }
          }
        }
      } else if (this.rootElement) {
        const classOnLeave = untracked(() => this.appNgIfAnimateClassOnLeave());
        if (!firstRun) {
          if (classOnLeave) {
            removeListener = this.onAnimationEnd(
              this.rootElement,
              () => {
                hideElement?.();
                if (this.rootElement) {
                  this.renderer.removeClass(this.rootElement, classOnLeave);
                }
              }
            );
            this.renderer.addClass(this.rootElement, classOnLeave);
          } else {
            this.logger.error('No animation class for leaving was specified');
            hideElement?.();
          }
        } else {
          // on first run, if element should be hidden, hide it immediately to not run animation on first load.
          hideElement?.();
        }
      }
      cleanUp(() => removeListener?.());
      firstRun = false;
    });

    // On appNgIfAnimateClass change, if template is rendered, update class list.
    let currentAnimateClass: string | null = null;
    this.animationClassUpdateEffect = effect(() => {
      const animateClass = this.appNgIfAnimateClass();
      if (this.rootElement) {
        if (currentAnimateClass && this.rootElement.classList.contains(currentAnimateClass)) {
          this.renderer.removeClass(this.rootElement, currentAnimateClass);
        }
        if (animateClass) {
          this.renderer.addClass(this.rootElement, animateClass);
        }
      }
      currentAnimateClass = animateClass || null;
    });
  }

  ngOnDestroy(): void {
    this.animateEffect.destroy();
    this.animationClassUpdateEffect.destroy();
  }

  /** Manages animation event listeners
   * Listen to animation start & end.
   * When all animations end, remove listeners and call `onAnimationEnd` callback
   * When no animation runs before timeout, call `onAnimationEnd` and `onNoAnimation` callbacks
   * @returns a function to end animation and remove listeners early
   */
  private onAnimationEnd(element: HTMLElement, onAnimationEnd: () => void): () => void {
    let animationCount: number | null = null;
    const removeListenerFns: (() => void)[] = [];
    const removeListeners = () => {
      do {
        removeListenerFns.pop()?.();
      } while (removeListenerFns.length);
    };

    let onAnimationEndCalled = false;
    const callOnAnimationEnd = () => {
      if (!onAnimationEndCalled) {
        onAnimationEnd();
        removeListeners();
        onAnimationEndCalled = true;
      }
    };

    const startListener = this.renderer.listen(
      element,
      this.ANIMATION_START_EVENT_NAME,
      () => {
        animationCount = (animationCount ?? 0) + 1;
      }
    );
    const endListener = this.renderer.listen(
      element,
      this.ANIMATION_END_EVENT_NAME,
      () => {
        animationCount = (animationCount ?? 0) - 1;
        if (animationCount <= 0) {
          callOnAnimationEnd();
        }
      }
    );
    removeListenerFns.push(startListener, endListener);
    setTimeout(() => {
      if (animationCount == null) {
        this.logger.error('No animation ran.');
        callOnAnimationEnd();
      }
    }, this.ANIMATION_TIMEOUT_MS);
    return callOnAnimationEnd;
  }
}
